import { takeShareCode, clearShareCode } from "@/lib/share";
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Sparkles, Gamepad2, Gift, Users, Facebook, Coins } from "lucide-react";
import { HeroMessage, HeroPhone, useLandingStats } from "@/components/landing/AuthHero";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, API, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isStandalone } from "@/lib/push";
import { ReportErrorButton } from "@/components/ReportError";
import { Logo } from "@/components/Logo";
import { PageExplain } from "@/components/PageExplain";
import { getAdSource, clearAdSource } from "@/lib/adSource";
import { FacebookWelcome } from "@/components/FacebookWelcome";
import { usePageMeta } from "@/lib/seo";

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
);

const authUrl = (redirectUrl) => `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;

function useGoogleHandoff() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [nonce, setNonce] = useState(() => localStorage.getItem("itz_handoff"));

  useEffect(() => {
    if (!nonce) return undefined;
    const claim = async () => {
      try {
        const { data } = await api.post("/auth/handoff/claim", { nonce });
        if (data.pending) return;
        localStorage.removeItem("itz_handoff"); setNonce(null);
        setUser(data.user); navigate("/feed");
      } catch (e) {
        localStorage.removeItem("itz_handoff"); setNonce(null);
      }
    };
    const t = setInterval(claim, 2000);
    const onVis = () => document.visibilityState === "visible" && claim();
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [nonce, setUser, navigate]);

  const start = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (!isStandalone()) {
      window.location.href = authUrl(window.location.origin + "/feed");
      return;
    }
    // Home-screen app (iOS) has its own cookie jar: log in via browser and hand the session back by nonce.
    const popup = window.open("", "_blank");
    const { data } = await api.post("/auth/handoff/start");
    localStorage.setItem("itz_handoff", data.nonce); setNonce(data.nonce);
    const url = authUrl(`${window.location.origin}/feed?handoff=${data.nonce}`);
    if (popup) popup.location.href = url; else window.open(url, "_blank");
  };
  const cancel = () => { localStorage.removeItem("itz_handoff"); setNonce(null); };
  return { waiting: !!nonce, start, cancel };
}

const GoogleBtn = () => {
  const { waiting, start, cancel } = useGoogleHandoff();
  if (waiting) {
    return (
      <div data-testid="google-handoff-waiting" className="p-4 rounded-2xl bg-rose-50 border border-rose-100 text-sm text-rose-800 space-y-2">
        <p className="flex items-center gap-2 font-semibold"><Loader2 className="w-4 h-4 animate-spin" /> Warte auf Google-Anmeldung…</p>
        <p>Melde dich im geöffneten Fenster mit Google an und kehre danach hierher zurück – du wirst automatisch eingeloggt.</p>
        <button type="button" onClick={cancel} data-testid="google-handoff-cancel" className="text-xs font-semibold underline">Abbrechen</button>
      </div>
    );
  }
  return (
    <button type="button" onClick={start} data-testid="google-login-btn"
      className="w-full flex items-center justify-center gap-3 h-12 rounded-full border border-pink-200 bg-white font-semibold text-slate-700 hover:bg-rose-50 transition-colors duration-200">
      <GoogleIcon /> Weiter mit Google
    </button>
  );
};

const FacebookBtn = () => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    api.get("/auth/config").then(({ data }) => setOn(data.facebook_enabled)).catch(() => {});
    const err = new URLSearchParams(window.location.search).get("fb_error");
    if (err) toast.error(err);
  }, []);
  if (!on) return null;
  const ref = new URLSearchParams(window.location.search).get("ref") || localStorage.getItem("itz_ref") || "";
  const go = () => window.location.assign(`${API}/auth/facebook/start${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`);
  return (
    <button type="button" onClick={go} data-testid="facebook-login-btn"
      className="w-full flex items-center justify-center gap-3 h-12 rounded-full bg-[#1877F2] hover:bg-[#166FE5] font-semibold text-white transition-colors duration-200">
      <Facebook className="w-5 h-5 fill-white" strokeWidth={0} /> Mit Facebook anmelden
    </button>
  );
};

const PERKS = [[Gamepad2, "Aktiv sein & spielen = Punkte"], [Gift, "Punkte gegen Geschenke"], [Users, "Freunde & Feed"]];

const ShellAside = () => {
  return (
    <div className="hidden lg:flex lg:sticky lg:top-0 lg:h-screen relative overflow-hidden bg-[#FFF5F8] border-r border-rose-100 flex-col justify-center px-12 xl:px-16 py-12" data-testid="auth-aside">
      <div className="absolute -top-40 -left-40 w-[34rem] h-[34rem] rounded-full bg-rose-200/50 blur-3xl" />
      <div className="absolute -bottom-40 -right-24 w-[30rem] h-[30rem] rounded-full bg-fuchsia-200/40 blur-3xl" />
      <div className="relative grid xl:grid-cols-[1fr_auto] gap-10 xl:gap-14 items-center">
        <HeroMessage compact as="h2" />
        <HeroPhone />
      </div>
    </div>
  );
};

const MobilePerks = () => (
  <div className="lg:hidden flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5" data-testid="auth-mobile-perks">
    {PERKS.map(([Icon, t]) => <span key={t} className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white border border-pink-200 text-xs font-bold text-rose-600 shadow-sm"><Icon className="w-3.5 h-3.5" /> {t}</span>)}
  </div>
);

const Shell = ({ children, explain }) => (
  <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-white">
    <ShellAside />
    <div className="relative overflow-hidden flex flex-col px-5 sm:px-12 py-6 sm:py-8 noise-bg">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-fuchsia-300/30 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -left-32 w-80 h-80 rounded-full bg-rose-300/30 blur-3xl pointer-events-none" />
      <div className="relative flex items-center justify-between"><Logo to="/" /><Link to="/" data-testid="auth-back-home" className="text-sm font-semibold text-slate-500 hover:text-rose-600">Zur Startseite</Link></div>
      <div className="relative flex-1 flex items-center">
        <div className="w-full max-w-md mx-auto space-y-5 py-6 fade-up">
          <MobilePerks />
          <div className="bg-white rounded-[2rem] border border-rose-100 shadow-2xl shadow-rose-500/10 p-6 sm:p-8 space-y-6" data-testid="auth-card">{children}</div>
          {explain}
          <div className="flex justify-center"><ReportErrorButton /></div>
        </div>
      </div>
    </div>
  </div>
);

const Divider = () => (
  <div className="flex items-center gap-3 text-xs text-slate-400 uppercase tracking-wider"><div className="flex-1 h-px bg-pink-100" />oder<div className="flex-1 h-px bg-pink-100" /></div>
);

const SubmitBtn = ({ loading, children, testId }) => (
  <button type="submit" disabled={loading} data-testid={testId}
    className="w-full h-12 rounded-full pink-gradient text-white font-bold pink-glow hover:opacity-95 active:scale-[0.98] transition-[transform,opacity] duration-200 flex items-center justify-center gap-2 disabled:opacity-60">
    {loading && <Loader2 className="w-4 h-4 animate-spin" />} {children}
  </button>
);

const LOGIN_STEPS = ["Melde dich mit E-Mail & Passwort oder mit Google an.", "Hol dir jeden Tag deinen Login-Bonus auf der Startseite ab.", "Noch kein Konto? Registriere dich kostenlos in 30 Sekunden."];

export function LoginForm() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const stats = useLandingStats();
  const bonus = stats?.settings?.points_signup_welcome || 0;
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data); navigate(sessionStorage.getItem("after_auth") || "/feed");
      if (data.account_restored) toast.success("Willkommen zurück! Dein Konto wurde wiederhergestellt 💖", { duration: 8000 });
    } catch (err) { setError(formatErr(err)); } finally { setLoading(false); }
  };
  return (
    <>
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight leading-[1.1] text-[#1E1B1D]">Willkommen zurück 👋</h1>
        <p className="text-slate-500 mt-1.5 text-sm">Melde dich an und sammle weiter Punkte.</p>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2"><Label>E-Mail-Adresse</Label><Input data-testid="login-email-input" type="email" required autoComplete="email" className="h-12 rounded-xl bg-white focus-visible:ring-rose-400" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div className="space-y-2"><div className="flex justify-between items-center"><Label>Passwort</Label><Link to="/passwort-vergessen" data-testid="forgot-password-link" className="text-xs font-semibold text-rose-600 hover:underline">Passwort vergessen?</Link></div><Input data-testid="login-password-input" type="password" required autoComplete="current-password" className="h-12 rounded-xl bg-white focus-visible:ring-rose-400" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
        <SubmitBtn loading={loading} testId="login-submit-btn">Anmelden</SubmitBtn>
      </form>
      <Divider />
      <GoogleBtn />
      <FacebookBtn />
      <div className="pt-1 border-t border-rose-50 text-center space-y-3">
        <p className="text-sm text-slate-500 pt-4">Neu hier?</p>
        {bonus > 0 && (
          <div className="shine glow-gold rounded-2xl bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 border border-amber-200 px-4 py-3 flex items-center justify-center gap-3" style={{ "--shine-delay": "1s" }} data-testid="signup-bonus">
            <span className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-white flex items-center justify-center shadow-md shadow-amber-200"><Coins className="w-5 h-5" /></span>
            <span className="text-left"><span className="block font-display text-2xl font-black text-amber-700 leading-none" data-testid="signup-bonus-points">+{bonus} Willkommenspunkte</span><span className="block text-[11px] font-semibold text-amber-700/80 mt-1">geschenkt bei deiner Registrierung</span></span>
          </div>
        )}
        <Link to="/registrieren" data-testid="goto-register-link" className="w-full h-12 rounded-full border-2 border-rose-200 text-rose-600 font-bold flex items-center justify-center hover:bg-rose-50 hover:border-rose-300 transition-colors">Kostenlos registrieren</Link>
      </div>
    </>
  );
}

export function LoginPage() {
  usePageMeta({ title: "Login", description: "Melde dich bei der itzPlay Community an – posten, spielen, Punkte sammeln und tolle Prämien holen." });
  const { user } = useAuth();
  if (user) return <Navigate to={sessionStorage.getItem("after_auth") || "/feed"} replace />;
  return (
    <Shell explain={<PageExplain testId="login-explain" steps={LOGIN_STEPS} />}>
      <LoginForm />
    </Shell>
  );
}

const FORGOT_STEPS = ["Gib die E-Mail-Adresse deines Kontos ein.", "Wir schicken dir einen Link – schau auch im Spam-Ordner nach.", "Tippe auf den Link und lege ein neues Passwort fest. Der Link ist 1 Stunde gültig."];

export function ForgotPage() {
  usePageMeta({ title: "Passwort vergessen", noindex: true });
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { setError(formatErr(err)); } finally { setLoading(false); }
  };
  return (
    <Shell explain={<PageExplain testId="forgot-explain" steps={FORGOT_STEPS} />}>
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight uppercase leading-[1.08]">Passwort vergessen?</h1>
        <p className="text-slate-500 mt-2">Kein Problem – wir schicken dir einen Link zum Zurücksetzen.</p>
      </div>
      {sent ? (
        <div data-testid="forgot-success" className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
          Falls ein Konto mit <b>{email}</b> existiert, haben wir dir gerade eine E-Mail geschickt. Bitte prüfe dein Postfach.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div className="space-y-2"><Label>E-Mail</Label><Input data-testid="forgot-email-input" type="email" required className="h-12 rounded-xl bg-white" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          {error && <p className="text-sm text-red-600" data-testid="forgot-error">{error}</p>}
          <SubmitBtn loading={loading} testId="forgot-submit-btn">Link senden</SubmitBtn>
        </form>
      )}
      <p className="text-sm text-center text-slate-500"><Link to="/login" className="text-rose-600 font-semibold" data-testid="forgot-back-login">Zurück zur Anmeldung</Link></p>
    </Shell>
  );
}

const RESET_STEPS = ["Wähle ein neues Passwort mit mindestens 6 Zeichen.", "Gib es zur Sicherheit zweimal ein.", "Danach meldest du dich mit dem neuen Passwort an."];

export function ResetPage() {
  usePageMeta({ title: "Neues Passwort", noindex: true });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw.a !== pw.b) { setError("Die Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw.a });
      toast.success("Passwort geändert! Du kannst dich jetzt anmelden.");
      navigate("/login");
    } catch (err) { setError(formatErr(err)); } finally { setLoading(false); }
  };
  return (
    <Shell explain={<PageExplain testId="reset-explain" steps={RESET_STEPS} />}>
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight uppercase leading-[1.08]">Neues Passwort</h1>
        <p className="text-slate-500 mt-2">Fast geschafft!</p>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div className="space-y-2"><Label>Neues Passwort</Label><Input data-testid="reset-password-input" type="password" required minLength={6} className="h-12 rounded-xl bg-white" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} /></div>
        <div className="space-y-2"><Label>Passwort wiederholen</Label><Input data-testid="reset-password-confirm-input" type="password" required minLength={6} className="h-12 rounded-xl bg-white" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} /></div>
        {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
        <SubmitBtn loading={loading} testId="reset-submit-btn">Passwort speichern</SubmitBtn>
      </form>
    </Shell>
  );
}

const REGISTER_STEPS = ["Erstelle dein kostenloses Konto mit E-Mail oder Google.", "Hast du einen Einladungscode von einem Freund? Trag ihn ein – dein Freund bekommt Bonuspunkte!", "Danach kannst du sofort posten, spielen und Punkte sammeln."];

export function RegisterPage() {
  usePageMeta({ title: "Kostenlos registrieren", description: "Werde kostenlos Mitglied der itzPlay Community: Das soziale Netzwerk von itzcouple: Sammle Punkte für Aktivitäten, Spiele und Einladungen und tausche sie gegen tolle Prämien." });
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", referral_code: params.get("ref") || localStorage.getItem("itz_ref") || "" });
  const [referrer, setReferrer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) localStorage.setItem("itz_ref", ref);
  }, [params]);

  useEffect(() => {
    if (!form.referral_code) { setReferrer(null); return; }
    const t = setTimeout(() => api.get(`/public/referrer/${form.referral_code}`).then(({ data }) => setReferrer(data.name)).catch(() => setReferrer(null)), 300);
    return () => clearTimeout(t);
  }, [form.referral_code]);
  const [inviteBonus, setInviteBonus] = useState(0);
  useEffect(() => { if (referrer) api.get("/public/stats").then(({ data }) => setInviteBonus(data.settings?.points_invite_welcome ?? 0)).catch(() => {}); }, [referrer]);

  if (user) return <Navigate to={sessionStorage.getItem("after_auth") || "/feed"} replace />;

  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/register", { ...form, referral_code: form.referral_code || null, share_code: takeShareCode(), source: getAdSource() });
      localStorage.removeItem("itz_ref"); clearShareCode();
      clearAdSource();
      setUser(data); navigate(sessionStorage.getItem("after_auth") || "/feed");
    } catch (err) { setError(formatErr(err)); } finally { setLoading(false); }
  };

  return (
    <Shell explain={<PageExplain testId="register-explain" steps={REGISTER_STEPS} />}>
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight uppercase leading-[1.08]">Werde Teil der Community</h1>
        <p className="text-slate-500 mt-2">Kostenlos registrieren & direkt loslegen.</p>
      </div>
      {referrer && (
        <div data-testid="referrer-banner" className="flex items-start gap-2 p-3 rounded-xl bg-rose-50 border border-rose-100 text-sm text-rose-700">
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0" /><span>Du wurdest von <b>{referrer}</b> eingeladen!{inviteBonus > 0 && <> Als Startgeschenk bekommst du <b data-testid="invite-bonus">+{inviteBonus} Punkte</b>.</>}</span>
        </div>
      )}
      <FacebookWelcome compact />
      <GoogleBtn />
      <FacebookBtn />
      <Divider />
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="space-y-2"><Label>Name</Label><Input data-testid="register-name-input" required className="h-12 rounded-xl bg-white" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div className="space-y-2"><Label>E-Mail</Label><Input data-testid="register-email-input" type="email" required className="h-12 rounded-xl bg-white" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div className="space-y-2"><Label>Passwort (min. 6 Zeichen)</Label><Input data-testid="register-password-input" type="password" required minLength={6} className="h-12 rounded-xl bg-white" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        <div className="space-y-2"><Label>Einladungscode (optional)</Label><Input data-testid="register-referral-input" className="h-12 rounded-xl bg-white uppercase" value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value.trim() })} /></div>
        {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
        <p className="text-xs text-slate-500 leading-relaxed" data-testid="register-legal-hint">Mit der Registrierung akzeptierst du unsere <Link to="/agb" className="font-semibold text-rose-600 hover:underline">AGB</Link> und <Link to="/nutzungsbedingungen" className="font-semibold text-rose-600 hover:underline">Nutzungsbedingungen</Link>. Infos zum Umgang mit deinen Daten findest du in der <Link to="/datenschutz" className="font-semibold text-rose-600 hover:underline">Datenschutzerklärung</Link>.</p>
        <SubmitBtn loading={loading} testId="register-submit-btn">Konto erstellen</SubmitBtn>
      </form>
      <p className="text-sm text-center text-slate-500">Schon dabei? <Link to="/login" className="text-rose-600 font-semibold" data-testid="goto-login-link">Anmelden</Link></p>
    </Shell>
  );
}
